// npm
import React from 'react'
import PropTypes from 'prop-types'

// material-ui
import withStyles from '@material-ui/core/styles/withStyles'

// utils
import {keyListener, KEYS} from 'utils/key-listener'

const TAB_INDENT = '  ';

// left/right arrows are global question-navigation shortcuts - suspend them while typing, the
// same as every other text input in the player (see workings.jsx)
const disableArrowKeyListeners = () => {
	keyListener.disable(KEYS.LEFT_ARROW);
	keyListener.disable(KEYS.RIGHT_ARROW);
};

const enableArrowKeyListeners = () => {
	keyListener.enable(KEYS.LEFT_ARROW);
	keyListener.enable(KEYS.RIGHT_ARROW);
};


// CodeEditor (not connected to styles)
// --------------------------------------------------------------------------

// a plain monospace textarea with a synced line-number gutter - deliberately simple (no syntax
// highlighting/autocomplete), just enough to let a learner write a readable code fragment
class CodeEditor extends React.Component {
	constructor(props) {
		super(props);
		this.gutterRef = React.createRef();
		this.handleChange = this.handleChange.bind(this);
		this.handleScroll = this.handleScroll.bind(this);
		this.handleKeyDown = this.handleKeyDown.bind(this);
	}

	handleChange(e) {
		this.props.onChange(e.target.value);
	}

	handleScroll(e) {
		if (this.gutterRef.current) {
			this.gutterRef.current.scrollTop = e.target.scrollTop;
		}
	}

	// Tab normally moves focus away from a textarea, which makes writing indented code
	// impractical - insert a soft tab at the cursor instead, like a real code editor
	handleKeyDown(e) {
		if (e.key !== 'Tab') {
			return;
		}

		e.preventDefault();
		const {value} = this.props;
		const {selectionStart, selectionEnd} = e.target;

		const newValue = value.substring(0, selectionStart) + TAB_INDENT + value.substring(selectionEnd);
		const cursorPosition = selectionStart + TAB_INDENT.length;

		this.props.onChange(newValue);
		this.pendingCursorPosition = cursorPosition;
	}

	componentWillUnmount() {
		// unmounting while focused doesn't fire onBlur, so don't leave navigation disabled
		enableArrowKeyListeners();
	}

	componentDidUpdate() {
		if (this.pendingCursorPosition != null && this.props.codeRef.current) {
			this.props.codeRef.current.setSelectionRange(this.pendingCursorPosition, this.pendingCursorPosition);
			this.pendingCursorPosition = null;
		}
	}

	render() {
		const {classes, value, codeRef} = this.props;
		const lineCount = value ? value.split('\n').length : 1;

		return (
			<div className={classes.container}>
				<div className={classes.gutter} ref={this.gutterRef}>
					{Array.from({length: lineCount}, (_, index) => (
						<div key={index} className={classes.lineNumber}>{index + 1}</div>
					))}
				</div>
				<textarea
					ref={codeRef}
					className={classes.textarea}
					value={value}
					onChange={this.handleChange}
					onScroll={this.handleScroll}
					onKeyDown={this.handleKeyDown}
					onFocus={disableArrowKeyListeners}
					onBlur={enableArrowKeyListeners}
					spellCheck={false}
					wrap="off"
				/>
			</div>
		);
	}
}

CodeEditor.propTypes = {
	value: PropTypes.string.isRequired,
	onChange: PropTypes.func.isRequired,
	codeRef: PropTypes.object
};


// CodeEditor (connected to styles)
// --------------------------------------------------------------------------

const MONOSPACE_FONT = 'Consolas, Monaco, "Courier New", monospace';
const FONT_SIZE = 14;
const LINE_HEIGHT = 20;

const styles = ({palette, spacing}) => ({
	container: {
		display: 'flex',
		flexDirection: 'row',
		flexGrow: 1,
		minHeight: 0,
		margin: spacing.unit,
		marginBottom: 0,
		border: `1px solid ${palette.background.contrastText}`,
		borderRadius: 4,
		overflow: 'hidden'
	},
	gutter: {
		flexShrink: 0,
		overflow: 'hidden',
		padding: `${spacing.unit}px 0`,
		backgroundColor: palette.background.default,
		borderRight: `1px solid ${palette.background.contrastText}`,
		userSelect: 'none'
	},
	lineNumber: {
		padding: `0 ${spacing.unit}px`,
		fontFamily: MONOSPACE_FONT,
		fontSize: FONT_SIZE,
		lineHeight: `${LINE_HEIGHT}px`,
		color: palette.text.secondary,
		textAlign: 'right'
	},
	textarea: {
		flexGrow: 1,
		minWidth: 0,
		padding: spacing.unit,
		border: 'none',
		outline: 'none',
		resize: 'none',
		fontFamily: MONOSPACE_FONT,
		fontSize: FONT_SIZE,
		lineHeight: `${LINE_HEIGHT}px`,
		color: palette.background.contrastText,
		backgroundColor: 'transparent'
	}
});

CodeEditor = withStyles(styles)(CodeEditor);


// Export
// --------------------------------------------------------------------------
export {CodeEditor}
